import { useState, useRef, useEffect } from "react";
import downIcon from "../../../assets/down-icon.svg";
import searchIcon from "../../../assets/search-new-icon.svg";

function normalizeSearchLabel(s) {
  return String(s ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

export function FilterDropdown({ placeholder, options = [], value, setValue, disabled = false, clearValue = "", labelMap = {}, enableSearch = false, valueTitle, fullWidth = false, hideLabel = false, keepOpenOnSelect = false, variant = "default", selectedItems = [] }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const ref = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && enableSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
    if (!open) {
      setSearchQuery("");
      setHighlightedIndex(-1);
    }
  }, [open, enableSearch]);

  const handleClear = (e) => {
    e.stopPropagation();
    setValue(clearValue);
    setOpen(false);
  };

  // Filter options — trim/collapse spaces so trailing spaces don’t hide matches
  const searchNeedle = normalizeSearchLabel(searchQuery);
  const filteredOptions = searchNeedle
    ? options.filter((opt) => {
        const label = labelMap[opt] || opt;
        return normalizeSearchLabel(label).includes(searchNeedle);
      })
    : options;

  return (
    <div ref={ref} className={`relative ${fullWidth ? "w-full min-w-0" : "min-w-[200px] max-w-[280px]"}`}>
      <div
        onClick={() => !disabled && setOpen(!open)}
        className={`flex items-center justify-between gap-3
        ${variant === "panel"
          ? "h-[70px] px-6 pr-36 border-2 border-solid border-[#E0E0E0] rounded-[40px] font-lufga text-[16px]"
          : "px-6 py-4 border border-solid border-[#E8E8E899] rounded-full"
        }
        ${disabled ? "bg-white cursor-not-allowed " : "bg-white cursor-pointer"}`}
      >
        <span
          className={`min-w-0 flex-1 truncate ${value ? "text-gray-700" : "text-gray-400"}`}
          title={valueTitle ?? value ?? undefined}
        >
          {value ? (labelMap[value] || value) : placeholder}
        </span>
        {variant !== "panel" && value && !hideLabel && (
          <span className="font-lufga text-[13px] text-gray-400 pointer-events-none shrink-0">
            {placeholder}
          </span>
        )}
        <div className="flex items-center gap-1 shrink-0">
          {variant !== "panel" && value && !disabled && (
            <span
              onClick={handleClear}
              className="flex items-center justify-center w-5 h-5 rounded-full
                hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors
                text-xs font-bold cursor-pointer"
              title="Clear selection"
            >
              &#x2715;
            </span>
          )}
          {variant !== "panel" && (
            <img
              src={downIcon}
              alt="down-icon"
              className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            />
          )}
        </div>
      </div>
      {variant === "panel" && (
        <>
          {value && !hideLabel && (
            <span className="absolute right-20 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
              {placeholder}
            </span>
          )}
          {value && !disabled && (
            <span
              onClick={(e) => { e.stopPropagation(); handleClear(e); }}
              className="absolute right-12 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 rounded-full
                hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors
                text-xs font-bold cursor-pointer"
              title="Clear selection"
            >
              &#x2715;
            </span>
          )}
          <img
            src={downIcon}
            alt="down"
            className={`absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform ${open ? "rotate-180" : ""}`}
          />
        </>
      )}

      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
          style={{ minWidth: '100%' }}
        >
          {/* Search Input */}
          {enableSearch && (
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 z-10">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <img
                  src={searchIcon}
                  alt="search"
                  className="w-4 h-4 flex-shrink-0 opacity-50"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setHighlightedIndex(-1); }}
                  onKeyDown={(e) => {
                    if (filteredOptions.length === 0) return;
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setHighlightedIndex((prev) => prev < filteredOptions.length - 1 ? prev + 1 : 0);
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setHighlightedIndex((prev) => prev > 0 ? prev - 1 : filteredOptions.length - 1);
                    } else if (e.key === "Enter" && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                      e.preventDefault();
                      const opt = filteredOptions[highlightedIndex];
                      setValue(opt === value ? clearValue : opt);
                      if (!keepOpenOnSelect) setOpen(false);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Search..."
                  className="w-full border-none outline-none bg-transparent text-sm text-gray-700 placeholder-gray-400"
                />
                {searchQuery && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchQuery("");
                      searchInputRef.current?.focus();
                    }}
                    className="text-gray-400 hover:text-gray-600 text-xs font-bold"
                  >
                    &#x2715;
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="max-h-[280px] overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, index) => {
                const isSelected = opt === value;
                const isInSelectedItems = Array.isArray(selectedItems) && selectedItems.includes(opt);
                const isHighlighted = highlightedIndex === index;
                const showAsSelected = isSelected || isInSelectedItems;
                return (
                  <div
                    key={`${opt}-${index}`}
                    ref={isHighlighted ? (el) => el?.scrollIntoView({ block: "nearest", behavior: "smooth" }) : undefined}
                    onClick={() => {
                      if (isSelected) {
                        setValue(clearValue);
                      } else {
                        setValue(opt);
                      }
                      if (!keepOpenOnSelect) setOpen(false);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`px-6 py-3 text-sm cursor-pointer flex items-center justify-between
                      ${showAsSelected
                        ? "bg-[#E8F5E9] text-[#2E7D32] font-medium hover:bg-[#C8E6C9]"
                        : isHighlighted
                          ? "bg-[#F0F9F4] ring-1 ring-inset ring-[#5AC69F]"
                          : "hover:bg-[#F4FBF8]"
                      }`}
                    title={labelMap[opt] && labelMap[opt] !== opt ? opt : undefined}
                  >
                    <span className="min-w-0 truncate">{labelMap[opt] || opt}</span>
                    {showAsSelected && (
                      <span className="text-[#2E7D32] font-bold" title="Selected">&#x2713;</span>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-6 py-4 text-sm text-gray-400 text-center">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
